import { useMemo } from 'react'

import { differenceInCalendarDays } from 'date-fns'

import { usePublishViewFilterTracking } from '~publish/hooks/usePublishViewFilterTracking'

import type { BoardState } from './useBoardState'

type TrackedBoardFilters = Pick<
  BoardState,
  | 'setGroupBy'
  | 'setChannelIds'
  | 'setTagIds'
  | 'setStatuses'
  | 'setDateRange'
  | 'clearDateRange'
>

/**
 * Returns the board filter setters, each of which also sends
 * `Publish View Filtered`. Use them only as handlers for user input.
 */
export function useTrackedBoardFilters(state: BoardState): TrackedBoardFilters {
  const track = usePublishViewFilterTracking('board')

  return useMemo(
    () => ({
      setGroupBy: (groupBy): void => {
        if (groupBy === state.groupBy) return
        state.setGroupBy(groupBy)
        track({ filterType: 'groupBy', action: 'apply', filterValue: groupBy })
      },
      // Counts only: channel and tag ids would make the event high-cardinality.
      setChannelIds: (channelIds): void => {
        state.setChannelIds(channelIds)
        track({
          filterType: 'channel',
          action: channelIds.length ? 'apply' : 'clear',
          selectedCount: channelIds.length,
        })
      },
      setTagIds: (tagIds): void => {
        state.setTagIds(tagIds)
        track({
          filterType: 'tag',
          action: tagIds.length ? 'apply' : 'clear',
          selectedCount: tagIds.length,
        })
      },
      setStatuses: (statuses): void => {
        state.setStatuses(statuses)
        track({
          filterType: 'status',
          action: statuses.length ? 'apply' : 'clear',
          filterValue: statuses.length ? statuses.join(',') : undefined,
          selectedCount: statuses.length,
        })
      },
      setDateRange: (startDate, endDate): void => {
        state.setDateRange(startDate, endDate)
        track({
          filterType: 'dateRange',
          action: 'apply',
          filterValue: String(differenceInCalendarDays(endDate, startDate) + 1),
        })
      },
      clearDateRange: (): void => {
        state.clearDateRange()
        track({ filterType: 'dateRange', action: 'clear' })
      },
    }),
    [state, track],
  )
}
